import { z } from 'zod';

import { LocalDateSchema, LocalTimeSchema } from '../admin/attendance';
import { IsoDateTimeSchema } from '../common/datetime';
import { TimezoneSchema } from '../common/fields';
import { SiteId } from '../common/ids';
import {
  AbsenceTypeSchema,
  DayDisplayStatusSchema,
  JustificationKindSchema,
  localDayDiff,
} from '../common/schedules';

/**
 * 008 · "Mi horario", realm staff (contracts/staff-api.md S1): `GET /attendance/days`.
 *
 * Hacia Staff **nunca** viajan nombres ni ids de administradores, `result_hash`, versiones de
 * evaluación o de asignación, ids de plantilla/versión, `external_ref`, `company_id`, `worker_id`
 * ni la clave de idempotencia. El servidor arma el DTO campo a campo
 * (`evaluation/staff.mapper.ts`); si se equivocara, el serializador descarta lo que sobre.
 */

/** Días máximos entre `from_day` y `to_day` (`to − from ≤ 31`). */
export const STAFF_DAYS_MAX_SPAN = 31;

/**
 * Query de `GET /attendance/days`. Defecto `[hoy − 13, hoy + 7]` ("hoy" = día operativo de la sede
 * principal). Los topes relativos a hoy (`from_day ≥ hoy − 59`, `to_day ≤ hoy + 13`) los aplica el
 * servidor; aquí solo el orden y el largo cuando llegan ambos.
 */
export const StaffDaysQuerySchema = z
  .object({
    from_day: LocalDateSchema.optional(),
    to_day: LocalDateSchema.optional(),
  })
  .superRefine((q, ctx) => {
    if (!q.from_day || !q.to_day) return;
    const diff = localDayDiff(q.from_day, q.to_day);
    if (diff < 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['to_day'],
        message: 'La fecha final no puede ser anterior a la inicial',
      });
    } else if (diff > STAFF_DAYS_MAX_SPAN) {
      ctx.addIssue({
        code: 'custom',
        path: ['to_day'],
        message: `El período puede tener como máximo ${STAFF_DAYS_MAX_SPAN} días`,
      });
    }
  });
export type StaffDaysQuery = z.infer<typeof StaffDaysQuerySchema>;

/** Turno tal como lo ve el trabajador (horas en la zona de la sede del turno). */
export const StaffShiftSchema = z.object({
  name: z.string(),
  code: z.string(),
  start_time: LocalTimeSchema,
  end_time: LocalTimeSchema,
  crosses_midnight: z.boolean(),
  site: z.object({ id: SiteId, name: z.string() }),
  time_zone: TimezoneSchema,
});
export type StaffShift = z.infer<typeof StaffShiftSchema>;

/** Plan del día: `NONE` = sin asignación. */
export const StaffPlanKindSchema = z.enum(['SHIFT', 'REST', 'OFF', 'NONE']);
export type StaffPlanKind = z.infer<typeof StaffPlanKindSchema>;

/** Señales que ve el trabajador (subconjunto de `EvaluationFlag`). */
export const StaffEvaluationFlagSchema = z.enum([
  'INCOMPLETE',
  'OTHER_SITE',
  'PARTIAL_ABSENCE',
  'LATE_JUSTIFIED',
  'EARLY_LEAVE_JUSTIFIED',
  'ABSENCE_JUSTIFIED',
]);
export type StaffEvaluationFlag = z.infer<typeof StaffEvaluationFlagSchema>;

export const StaffDayEvaluationSchema = z.object({
  /** `PENDING` mientras el día no es final. */
  display_status: DayDisplayStatusSchema,
  late_minutes: z.number().int().nonnegative(),
  early_leave_minutes: z.number().int().nonnegative(),
  worked_minutes: z.number().int().nonnegative(),
  flags: z.array(StaffEvaluationFlagSchema),
});
export type StaffDayEvaluation = z.infer<typeof StaffDayEvaluationSchema>;

export const StaffDaySchema = z.object({
  day: LocalDateSchema,
  plan: z.object({ kind: StaffPlanKindSchema, shifts: z.array(StaffShiftSchema) }),
  holiday: z.object({ name: z.string() }).nullable(),
  absence: z
    .object({
      type: AbsenceTypeSchema,
      partial: z.boolean(),
      start_time: LocalTimeSchema.nullable(),
      end_time: LocalTimeSchema.nullable(),
    })
    .nullable(),
  /** `null` para días futuros o fuera de la relación laboral. */
  evaluation: StaffDayEvaluationSchema.nullable(),
  /** Lo que el servidor permite justificar hoy (puede ser `[]`). */
  justify: z.object({ kinds: z.array(JustificationKindSchema) }),
});
export type StaffDay = z.infer<typeof StaffDaySchema>;

/** `200` de `GET /attendance/days`. */
export const StaffDaysResponseSchema = z.object({
  /** Alguna asignación en ±60 días. */
  has_schedule: z.boolean(),
  today: LocalDateSchema,
  /** Ascendente. */
  days: z.array(StaffDaySchema),
  /** `regularization_window_days` de la empresa (plazo para justificar). */
  window_days: z.number().int().positive(),
  server_time: IsoDateTimeSchema,
});
export type StaffDaysResponse = z.infer<typeof StaffDaysResponseSchema>;
